/**
 * Parse Options Mapper
 * Converts the persisted, UI-shaped parse settings into the gateway wire
 * ParseOptions. Pure and side-effect free. All validation lives here.
 *
 * A wire field is emitted ONLY when it deviates from the gateway default,
 * so requests stay backward-compatible (issue #57).
 */

import type { ParseOptions } from './gateway-service';

export interface ParseSettings {
  /** LaTeX formula recognition (do_formula_enrichment). */
  formulaRecognition: boolean;
  ocr: {
    /** 'auto' = gateway default, 'off' = do_ocr:false, 'force' = force_ocr:true. */
    mode: 'auto' | 'off' | 'force';
    /** ISO 639-3 codes; empty ⇒ omit ocr_lang. */
    languages: string[];
  };
  table: {
    structure: boolean; // do_table_structure
    mode: 'accurate' | 'fast'; // table_mode
  };
  /** [from,to] 1-indexed page pair; null ⇒ all pages. */
  pageRange: { from: number; to: number } | null;
}

export const DEFAULT_PARSE_SETTINGS: ParseSettings = {
  formulaRecognition: true,
  ocr: { mode: 'auto', languages: [] },
  table: { structure: true, mode: 'accurate' },
  pageRange: null,
};

/** Thrown when settings are invalid and must not be sent to the gateway. */
export class ParseSettingsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ParseSettingsError';
  }
}

export function toParseOptions(settings: ParseSettings): ParseOptions {
  const options: ParseOptions = {};

  if (settings.formulaRecognition) {
    options.do_formula_enrichment = true;
  }

  if (settings.ocr.mode === 'off') {
    options.do_ocr = false;
  } else if (settings.ocr.mode === 'force') {
    options.do_ocr = true;
    options.force_ocr = true;
  }
  if (settings.ocr.languages.length > 0) {
    options.ocr_lang = [...settings.ocr.languages];
  }

  if (settings.table.structure === false) {
    options.do_table_structure = false;
  }
  if (settings.table.mode === 'fast') {
    options.table_mode = 'fast';
  }

  if (settings.pageRange !== null) {
    const { from, to } = settings.pageRange;
    if (
      !Number.isInteger(from) ||
      !Number.isInteger(to) ||
      from < 1 ||
      to < from
    ) {
      throw new ParseSettingsError(
        `Invalid page range: from=${from}, to=${to}. Require integers with from >= 1 and to >= from.`
      );
    }
    options.page_range = [from, to];
  }

  return options;
}
